// web-commands.ts — the PER-APP half of the web shape: maps oriora_round_card's Tauri data
// commands (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore.
// Plain CRUD is a few lines each. The one derived command (compute_signals) is the app's
// "paid draw" computed in signals.rs — on web it returns a minimal shell for now; a faithful web
// build reimplements that signal logic in JS (the known per-app wrinkle, see MACOS/web docs).

import type { CommandHandler, WebStore } from "./web-runtime";
import { browserSearch, browserFetch } from "./web-search-browser";

const ROUNDS = "rounds";
const BAG_CHANGES = "bag_changes";

function uuid(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}
function today(): string {
  const d = new Date();
  return `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    // ── Rounds ──
    list_rounds: ({ from, to, limit }) => {
      let rows = db.all(ROUNDS).filter((r) => {
        const d = String((r as { played_on?: string }).played_on ?? "");
        if (from && d < String(from)) return false;
        if (to && d > String(to)) return false;
        return true;
      });
      // Newest first (mirrors the native ORDER BY played_on DESC).
      rows.sort((a, b) =>
        String((b as { played_on?: string }).played_on ?? "").localeCompare(
          String((a as { played_on?: string }).played_on ?? ""),
        ),
      );
      const lim = typeof limit === "number" ? limit : 50000;
      return rows.slice(0, lim);
    },
    get_round: ({ id }) => db.get(ROUNDS, String(id)),
    create_round: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const holes = i.holes_played === 9 ? 9 : 18;
      const holeScores = Array.isArray(i.hole_scores) ? (i.hole_scores as number[]) : [];
      const total =
        holeScores.length > 0
          ? holeScores.reduce((a, b) => a + Number(b || 0), 0)
          : Number(i.total_score ?? 0);
      const row = {
        id: uuid(),
        played_on: String(i.played_on ?? today()),
        course: String(i.course ?? ""),
        holes_played: holes,
        hole_scores: holeScores,
        total_score: total,
        putts: (i.putts ?? null) as number | null,
        fairways_hit: (i.fairways_hit ?? null) as number | null,
        fairways_eligible: (i.fairways_eligible ?? null) as number | null,
        greens_in_reg: (i.greens_in_reg ?? null) as number | null,
        course_rating: (i.course_rating ?? null) as number | null,
        slope: (i.slope ?? null) as number | null,
        conditions: String(i.conditions ?? ""),
        partners: Array.isArray(i.partners) ? (i.partners as string[]) : [],
        notes: String(i.notes ?? ""),
        photo: "",
        created_at: nowIso(),
        updated_at: nowIso(),
      };
      return db.insert(ROUNDS, row as { id: string } & Record<string, unknown>);
    },
    update_round: ({ id, patch }) => {
      db.update(ROUNDS, String(id), { ...(patch as Record<string, unknown>), updated_at: nowIso() });
      return null;
    },
    // Attach a photo to a round — filed ON-DEVICE, never sent to the model (web mirror of the native command).
    set_round_photo: ({ id, photo }) => {
      db.update(ROUNDS, String(id), { photo: String(photo ?? ""), updated_at: nowIso() });
      return null;
    },
    delete_round: ({ id }) => {
      db.remove(ROUNDS, String(id));
      return null;
    },
    // ── Bag-change log ──
    list_bag_changes: ({ limit }) => {
      const rows = db.all(BAG_CHANGES).sort((a, b) =>
        String((b as { changed_on?: string }).changed_on ?? "").localeCompare(
          String((a as { changed_on?: string }).changed_on ?? ""),
        ),
      );
      const lim = typeof limit === "number" ? limit : 5000;
      return rows.slice(0, lim);
    },
    create_bag_change: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const row = {
        id: uuid(),
        changed_on: String(i.changed_on ?? today()),
        action: String(i.action ?? "added"),
        club: String(i.club ?? ""),
        notes: String(i.notes ?? ""),
        created_at: nowIso(),
      };
      return db.insert(BAG_CHANGES, row as { id: string } & Record<string, unknown>);
    },
    delete_bag_change: ({ id }) => {
      db.remove(BAG_CHANGES, String(id));
      return null;
    },
    // Derived season recap — native side computes rich signals in signals.rs. Web shell = valid-but-empty
    // so the UI never crashes; the AI recap feature is degraded until the signal logic is ported to JS.
    compute_signals: ({ today: forDate }) => ({
      schema: "round.signals.v1",
      generated_for: String(forDate ?? today()),
      overall: {
        total_rounds: 0,
        rounds_18: 0,
        rounds_9: 0,
        days_span: 0,
        first_date: null,
        last_date: null,
        avg_score_per_18: null,
        best_score_per_18: null,
        note: "",
      },
      scoring_trend: null,
      form_block: null,
      accuracy: {
        fairway_rate: null,
        fairway_rounds_tracked: 0,
        fairway_rate_recent: null,
        fairway_rate_prior: null,
        fairway_direction: "n/a",
        gir_per_round: null,
        gir_rounds_tracked: 0,
      },
      putting: null,
      courses: [],
      conditions: [],
      partners: [],
      bag: {
        current_clubs: [],
        current_count: 0,
        recent_changes: [],
        added_count: 0,
        retired_count: 0,
      },
    }),
  };
}
